import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const Perfil = () => {
  const { authData, updateUserName, logout } = useContext(AuthContext);
  const { token } = authData;
  const navigate = useNavigate();
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarNovaSenha, setShowConfirmarNovaSenha] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [userData, setUserData] = useState({
    nome: '',
    email: '',
  });
  const [editName, setEditName] = useState(false);
  const [newName, setNewName] = useState('');
  const [passwordData, setPasswordData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarNovaSenha: '',
  });

  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUserProfile = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/users/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserData(res.data);
      setNewName(res.data.nome);
      setNotice(null);
    } catch (err) {
      setNotice({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível carregar o perfil.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = async () => {
    if (newName.trim() === '') {
      setNotice({
        type: 'error',
        text: 'O nome não pode estar vazio.',
      });
      return;
    }

    setIsSavingName(true);

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update-name`,
        { nome: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUserData(res.data);
      updateUserName(res.data.nome);
      setEditName(false);
      setNotice({
        type: 'success',
        text: 'Nome atualizado com sucesso.',
      });
    } catch (err) {
      setNotice({
        type: 'error',
        text: err.response?.data?.msg || 'Erro ao atualizar o nome.',
      });
    } finally {
      setIsSavingName(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const { senhaAtual, novaSenha, confirmarNovaSenha } = passwordData;

    if (!senhaAtual || !novaSenha || !confirmarNovaSenha) {
      setNotice({
        type: 'error',
        text: 'Preencha todos os campos de senha.',
      });
      return;
    }

    if (novaSenha !== confirmarNovaSenha) {
      setNotice({
        type: 'error',
        text: 'A nova senha e a confirmação não correspondem.',
      });
      return;
    }

    setIsSavingPassword(true);

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update-password`,
        { senhaAtual, novaSenha },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPasswordData({ senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' });
      setNotice({
        type: 'success',
        text: res.data.msg,
      });
    } catch (err) {
      setNotice({
        type: 'error',
        text: err.response?.data?.msg || 'Erro ao atualizar a senha.',
      });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmDelete = window.confirm(
      'ATENÇÃO: esta ação é irreversível e todos os seus dados serão perdidos. Deseja continuar?'
    );

    if (!confirmDelete) {
      return;
    }

    setIsDeleting(true);

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/users/delete-account`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      logout();
      navigate('/', { replace: true });
    } catch (err) {
      setNotice({
        type: 'error',
        text: err.response?.data?.msg || 'Erro ao excluir a conta.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <section className="page-section">
        <div className="surface-card page-status">Carregando perfil...</div>
      </section>
    );
  }

  return (
    <section className="page-section">
      <div className="surface-card profile-hero">
        <div className="profile-hero__identity">
          <div className="avatar-badge">{userData.nome?.charAt(0)?.toUpperCase() || 'U'}</div>
          <div>
            <span className="eyebrow">Perfil</span>
            <h1 className="section-title">{userData.nome || 'Sua conta'}</h1>
            <p className="section-copy">{userData.email}</p>
          </div>
        </div>

        <div className="stats-grid stats-grid--compact">
          <div className="metric-card">
            <span className="metric-label">Conta</span>
            <strong className="metric-value">Ativa</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Tema</span>
            <strong className="metric-value">Adaptável</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Segurança</span>
            <strong className="metric-value">Com senha hash</strong>
          </div>
        </div>
      </div>

      {notice && (
        <div className={`notice notice--${notice.type}`} role="status">
          {notice.text}
        </div>
      )}

      <div className="profile-grid">
        <div className="surface-card profile-card">
          <div className="section-heading">
            <span className="eyebrow">Dados da conta</span>
            <h2 className="section-subtitle">Informações principais</h2>
          </div>

          {editName ? (
            <div className="inline-panel">
              <label className="field">
                <span className="field-label">Nome</span>
                <input
                  type="text"
                  className="field-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <div className="inline-actions">
                <button
                  type="button"
                  className="primary-button button-compact"
                  onClick={handleNameChange}
                  disabled={isSavingName}
                >
                  {isSavingName ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  type="button"
                  className="subtle-button button-compact"
                  onClick={() => {
                    setEditName(false);
                    setNewName(userData.nome);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-summary">
              <div>
                <span className="profile-summary__label">Nome</span>
                <strong>{userData.nome}</strong>
              </div>
              <button
                type="button"
                className="subtle-button button-compact"
                onClick={() => setEditName(true)}
              >
                Editar nome
              </button>
            </div>
          )}

          <div className="profile-summary">
            <div>
              <span className="profile-summary__label">E-mail</span>
              <strong>{userData.email}</strong>
            </div>
          </div>
        </div>

        <div className="surface-card profile-card">
          <div className="section-heading">
            <span className="eyebrow">Segurança</span>
            <h2 className="section-subtitle">Alterar senha</h2>
          </div>

          <form onSubmit={handlePasswordChange} className="auth-form">
            <input
              type="hidden"
              name="username"
              value={userData.email}
              autoComplete="username"
            />

            <label className="field">
              <span className="field-label">Senha atual</span>
              <div className="password-field">
                <input
                  type={showSenhaAtual ? 'text' : 'password'}
                  className="field-input"
                  value={passwordData.senhaAtual}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, senhaAtual: e.target.value })
                  }
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowSenhaAtual((currentState) => !currentState)}
                >
                  <i
                    className={`bi ${showSenhaAtual ? 'bi-eye-slash' : 'bi-eye'}`}
                  ></i>
                </button>
              </div>
            </label>

            <label className="field">
              <span className="field-label">Nova senha</span>
              <div className="password-field">
                <input
                  type={showNovaSenha ? 'text' : 'password'}
                  className="field-input"
                  value={passwordData.novaSenha}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, novaSenha: e.target.value })
                  }
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNovaSenha((currentState) => !currentState)}
                >
                  <i className={`bi ${showNovaSenha ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </button>
              </div>
            </label>

            <label className="field">
              <span className="field-label">Confirmar nova senha</span>
              <div className="password-field">
                <input
                  type={showConfirmarNovaSenha ? 'text' : 'password'}
                  className="field-input"
                  value={passwordData.confirmarNovaSenha}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmarNovaSenha: e.target.value,
                    })
                  }
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() =>
                    setShowConfirmarNovaSenha((currentState) => !currentState)
                  }
                >
                  <i
                    className={`bi ${
                      showConfirmarNovaSenha ? 'bi-eye-slash' : 'bi-eye'
                    }`}
                  ></i>
                </button>
              </div>
            </label>

            <button
              type="submit"
              className="primary-button button-block"
              disabled={isSavingPassword}
            >
              {isSavingPassword ? 'Atualizando...' : 'Atualizar senha'}
            </button>
          </form>
        </div>
      </div>

      <div className="surface-card danger-zone">
        <div>
          <span className="eyebrow">Zona de risco</span>
          <h2 className="section-subtitle">Excluir conta</h2>
          <p className="section-copy">
            Esta ação remove seu usuário e as listas associadas de forma
            permanente.
          </p>
        </div>
        <button
          type="button"
          className="danger-button"
          onClick={handleDeleteAccount}
          disabled={isDeleting}
        >
          {isDeleting ? 'Excluindo...' : 'Excluir conta'}
        </button>
      </div>
    </section>
  );
};

export default Perfil;
